import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/lib/seo";

export default function About() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "About", url: `${SITE_URL}/about` },
  ];
  return (
    <main>
      <Seo
        title="About the Atelier · Boutique Web Design Studio"
        description="LAK Atelier is a boutique web design studio in London creating bespoke, editorial websites for independent brands. Meet the studio behind the work."
        keywords="boutique web design studio, london web designer, bespoke website design, editorial web design, independent brand websites"
        breadcrumbs={crumbs}
      />
      <article className="pt-32 sm:pt-40 bg-lak-bg">
        <section className="lak-section pb-24">
          <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
            <Breadcrumbs items={crumbs} className="mb-10" />
            <Reveal><span className="eyebrow">About the atelier</span></Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.96] max-w-5xl">
                A quiet studio for <em className="italic text-lak-sage">considered brands.</em>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 max-w-2xl text-lg text-lak-soft">
                LAK Atelier is an independent web design studio based in London. We work with a small, considered list of clients each year, designing and building bespoke websites that feel quietly luxurious and unmistakably their own.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="lak-section py-24 bg-lak-secondary border-y border-black/10">
          <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-5">
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.04]">A note on how we work.</h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-10 lg:border-l border-black/10 space-y-6 text-lg text-lak-soft leading-relaxed">
              <p>We treat each project like the only one on our desk. There are no production lines, no junior hand-offs, and no off-the-shelf templates. Every site begins on a blank page and is composed with the same patience a flagship store receives.</p>
              <p>Our work is rooted in editorial design — typography first, photography close behind, and a quiet attention to every detail. We believe a well-considered website should feel inevitable, like it could never have been anything else.</p>
              <p>We stay with our clients long after launch. Hosting, security, ongoing SEO, and small refinements are included quietly in your monthly support, so the website grows with the business it represents.</p>
            </div>
          </div>
        </section>

        <section className="lak-section py-28 bg-lak-bg">
          <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 text-center">
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.04]">
                Begin with <em className="italic text-lak-sage">a short conversation.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/contact" className="mt-10 inline-flex items-center gap-3 px-10 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase">
                Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
              </Link>
            </Reveal>
          </div>
        </section>
      </article>
    </main>
  );
}
